import React, { useState, useEffect } from 'react';
import { Box, List } from '@mui/material';
import WhookEventModal from '../components/whook/WhookEventModal';
import WhookStreamHeader from '../components/whook/WhookStreamHeader';
import WhookEventItem from '../components/whook/WhookEventItem';

const agentToRegionMap = {
    'Global': 'HQ',
    'Los Angeles': 'North America',
    'Pasadena': 'North America',
    'Detroit': 'North America',
    'New York': 'North America',
    'Chicago': 'North America',
    'Atlanta': 'North America',
    'Austin': 'North America',
    'San Diego': 'North America',
    'Brooklyn': 'North America',
    'Bronx': 'North America',
    'Queens': 'North America',
    'Oakland': 'North America',
    'Phoenix': 'North America',
    'Dallas': 'North America',
    'Miami': 'North America',
    'Seattle': 'North America',
    'San Francisco': 'North America',
    'Houston': 'North America',
    'Tampa': 'North America',
    'Toronto': 'North America',
    'London': 'Europe',
    'Dublin': 'Europe',
    'Tokyo': 'Asia',
    'Mumbai': 'Asia',
    'Melbourne': 'Australia',
    'Manila': 'Asia',
    'Rio de Janeiro': 'South America',
    'Cape Town': 'Africa',
    'Nairobi': 'Africa'
};

const statusColors = {
    executed: 'success',
    approved: 'info',
    'awaiting approval': 'warning',
    confirmed: 'primary',
    learning: 'secondary',
    'on hold': 'error',
    processing: 'default',
    'needs action': 'error',
    shared: 'info',
    logged: 'secondary',
    initiated: 'primary',
    ordered: 'success'
};

// Internal functional roles (appear in modal metadata only)
const agentRoles = [
    'Outreach Agent', 'Booking Agent', 'Finance Agent', 'Logistics Agent',
    'Training Agent', 'Marketing Agent', 'Compliance Agent', 'Analytics Agent',
    'Sponsorship Agent', 'Support Agent'
];

const synonymsMap = {
    'Ontario': 'Toronto',
    'Ireland': 'Dublin',
    'São Paulo': 'Rio de Janeiro',
    'Brazil': 'Rio de Janeiro',
    'Delhi': 'Mumbai',
    'Mexico City': 'Global',
    'Mexico ': 'Global',
    'Johannesburg': 'Global',
    'Western Canada': 'Toronto',
    'California': 'Los Angeles',
    'South LA': 'Los Angeles',
    'East Bay': 'Oakland',
    'Compton': 'Los Angeles',
    'SoHo': 'New York',
    'Pacific Region': 'Global',
    'Setagaya': 'Tokyo',
    'Khayelitsha': 'Cape Town',
    'Quezon City': 'Manila',
    'all regions': 'Global',
    'Rio Agent': 'Rio de Janeiro'
};

const actions = [
    'Autonomous Action', 'Manual Override', 'System Trigger', 'Post-Session Review',
    'Venue Adjustment', 'Survey Sync', 'Payment Batch', 'Lead Reprocessing',
    'Pipeline Update', 'Training Trigger', 'Outreach Sync', 'Marketing Rotation',
    'Agent Promotion', 'Session Escalation', 'Creative Approval', 'Scholarship Sync',
    'Ad Placement Test', '3PL Routing', 'Inventory Alert', 'Campaign Audit',
    'Financial Audit', 'Margin Check', 'Weather Alert', 'Partnership'
];

const messages = [
    // 🔍 DATA INGESTION & DISCOVERY (AUTONOMOUS PIPELINE)
    '🌎 DeepResearch Orchestrator initiating cycle across 14 regions — balancing task load across ChatGPT-4o, Gemini 2.0 Ultra, Claude 3.5 Opus, Mistral Large, and Perplexity Pro.',
    '🤖 ChatGPT-4o Discovery Agent scraping Eventbrite (Los Angeles County) — 1,842 events indexed and vectorized.',
    '🧠 Gemini 2.0 Ultra Node parsed 1,120 Meetup pages across LA and San Diego — 102 recurring categories identified.',
    '📡 Claude 3.5 Opus Knowledge Crawler scanning Facebook Events API — found 1,674 unlisted gatherings near Glendale.',
    '🔍 Mistral Large DeepResearch Query #203 — “emerging youth enrichment programs United States” — returned 7,412 unique results across 32 metro areas.',
    '📊 Perplexity Pro Search Node analyzed Yelp, Google Maps, and Reddit community pages — 2,400 listings normalized and tagged.',
    '🌐 ChatGPT-4o Trend Engine cross-referenced Google Trends global spikes — “STEM Robotics Clubs for Kids” +22% WoW.',
    '🌍 Gemini 2.0 Ultra scraped TimeOut LA and London — 342 new “group experiences” classified.',
    '📑 Claude 3.5 Opus integrated Groupon + ClassPass listings — 210 new recreation categories normalized across multiple cities.',
    '📥 Mistral Large DeepResearch #311 merged regional Parks CSV datasets into MongoDB Venue Graph.',
    '🧮 Perplexity Pro Topic Engine vectorized 18,000 event titles for semantic similarity clustering.',
    '💡 ChatGPT-4o DeepResearch detected emerging keyword cluster: “Urban Gardening for Teens.”',
    '🎯 Gemini 2.0 Ultra Margin Scorer → Cluster Confidence 0.91 — flagged for pilot simulation.',
    '🧾 Claude 3.5 Opus cross-validated trend against MongoDB logs — growth curve confirmed in 5 markets.',
    '📊 Mistral Large aligned embeddings between Eventbrite + Meetup categories using cosine similarity 0.84.',
    '📈 Gemini 2.0 Ultra combined Google Trends + Reddit sentiment for “Esports Fitness” — confidence 0.88.',
    '🧬 Perplexity Pro correlated income density vs event frequency across 256 zip codes — targeting matrix built.',
    '📚 Claude 3.5 Opus parsed Education.com and SchoolDigger APIs → 680 after-school programs indexed.',
    '🌐 Mistral Large scraped LAUSD Extended Learning Portal — 190 school activities integrated into MongoDB.',
    '🧠 ChatGPT-4o DeepResearch Query #4512 → discovered “AI Music Production Bootcamps” trend in SoCal + London.',
    '🗂️ DeepResearch Router Decision: Claude 3.5 Opus selected over ChatGPT-4o (token ablation savings 28%).',
    '⚙️ Router rerouted task load to Gemini 2.0 after Claude latency threshold exceeded 3.2s.',
    '📡 Token Ablation Log #9823 — Gemini reused Claude vector cache — 31% token efficiency gain.',
    '🧩 DeepResearch Router optimizing query routing based on model cost history, token usage, and latency profiles.',
    '📈 MongoDB cross-reference confirmed overlapping clusters between Eventbrite, Meetup, and Reddit — consolidation triggered.',
    '🔎 DeepResearch Query #1452 (Perplexity Pro) found 97 unlisted “Free Teen Creative Workshops” in South LA.',
    '🧠 Claude semantic parser merged “Mental Health” + “Dance Fitness” → new hybrid category: “Flow Movement Labs.”',
    '🔬 Cross-model insight transfer complete — ChatGPT-4o received Claude embeddings via router sync.',
    '🧩 Token Ablation Engine training local routing heuristic (pre-SLM version 0.2).',
    '🌎 Regional Agents across LA, Chicago, London, and Tokyo compiled 12,300 new event signals for global deduplication.',

    // 🧩 CONCEPT CLUSTERING / SCORING
    '📊 Cluster Agent merged “Coding for Kids,” “STEM Builders,” and “Robotics Labs” → unified concept: “NextGen STEM.”',
    '🧩 Concept Graph expanded — 42 new relationships detected between fitness and creative classes.',
    '🧠 Margin Scorer prioritized categories with >65% yield based on equipment + staff cost models.',
    '📈 Functional Movement Labs cluster validated — high-affluence zip codes in 3 pilot regions.',
    '🧮 Topic similarity scan completed across 10,000 event descriptions (cross-validated by Claude + Gemini).',
    '🧠 Neural Clustering retrained — discovery embeddings up to 93% global accuracy.',
    '🧩 Niche category “Esports Coaching Camps” validated in Long Beach and Tokyo nodes.',
    '🧭 Cluster Engine scoring “Drone Photography Bootcamps” — confidence 0.87.',
    '📊 Overlap between “Youth Yoga Retreats” + “Mindfulness Workshops” merged → “Flow Series.”',
    '🧠 Cross-analysis: “Gardening + Nutrition” reclassified as hybrid Wellness cluster (confidence 0.89).',

    // 🏙 CITY-LEVEL AGENT COORDINATION
    '🏙 Los Angeles Agent aggregating daily discovery reports from 12 sub-agents.',
    '📍 Pasadena Agent escalating 3 high-signal pilots for venue matching.',
    '🏙 Glendale Agent syncing discovery data from Meetup + Eventbrite.',
    '🏙 Santa Monica Agent validating beach zone permits for outdoor pilot feasibility.',
    '🏙 New York Agent analyzing school gym permits — 72 new listings pulled from NYC Parks API.',
    '🏙 London Agent updating cultural permit database with 200 local listings.',
    '🏙 Miami Agent initiating cultural pilot sequence for “Latin Dance Bootcamps.”',
    '🏙 Chicago Agent flagged basketball saturation — redistributing ad budget.',
    '🌎 HQ Learning Node rolled up 120 regional discovery summaries for cross-model training.',
    '🗺️ Regional coordination log: 18 city agents linked to HQ Node for nightly embedding sync.',

    // 🏟 VENUE & INSTRUCTOR MATCHING
    '📍 Venue Agent located Roosevelt Park available Saturdays 2–6 PM for pilot “Urban Gardening.”',
    '📦 Logistics Agent verified 12 drone kits available in Glendale storage hub.',
    '🏫 Booking Agent requested quote from Compton Rec Center — awaiting callback.',
    '📋 Venue optimization score recalculated — Glendale > Pasadena (3.9 vs 3.6).',
    '🏟 27 school gyms auto-added to LA cluster after 3rd-party API sync.',
    '🧱 Facility Scouter scraped LAUSD + Chicago Public Schools portals — 96 gyms indexed.',
    '👥 Instructor Agent matched 7 qualified coaches within 10-mile radius.',
    '📞 Outreach Agent contacted East LA YMCA for gym lease availability — pending inspection.',
    '📑 Venue cost matrix updated — weekend premium +22%.',
    '🏗️ Unused lot in South Central identified for pop-up field pilot — site flagged for safety check.',

    // 💬 OUTREACH & NEGOTIATION
    '📞 Outreach Agent placed AI-assisted call to Roosevelt Park Facility Manager — confirmed interest.',
    '📧 Outreach Agent sent 5 proposals to LA Parks, Burbank Schools, and YMCA Glendale.',
    '💬 Voice Agent logged callback summary — awaiting city approval.',
    '📨 Chicago Parks outreach finalized — awaiting compliance clearance.',
    '🧾 Pasadena Facility Manager requested insurance documents before contract finalization.',
    '🧍 Human Override: Adidas approved local girls sports grant for South LA pilot.',
    '📱 SMS campaign deployed — 2,300 parents targeted for STEM trial registration.',
    '📬 48 follow-up messages queued for unresponsive recreation directors.',
    '🗣️ AI Voice Agent reattempting contact — session booking deadline approaching.',

    // 🚀 PILOT SIMULATION & LAUNCH
    '🧪 Simulation Engine running pilot “Drone Racing Workshop” — predicted 71% utilization rate.',
    '📊 Pilot Simulation: “Functional Movement Labs” — forecast 68% margin, breakeven 18 attendees.',
    '✅ Launch Agent confirmed rollout in 3 LA clusters (Pasadena, Glendale, Long Beach).',
    '🎯 Marketing Agent scheduling local ad rotation — CTR target 2.4%.',
    '💰 Finance Agent batch-processed 4 payments via Stripe integration.',
    '📅 Scheduling Agent cloned pilot schedule to Orange County — weekend offset applied.',
    '📦 Supply Chain Agent confirmed 3PL delivery ETA: 48 hrs before session.',
    '📋 Venue Roosevelt Park pilot completed — participant satisfaction 94%.',
    '🧾 Finance audit confirmed $10.4K net margin across Q2 LA pilots.',
    '🎟️ Waitlist conversion rate up 18% following TikTok rollout.',

    // 🌎 EXPANSION & CROSS-REGION ROLLOUTS
    '🌍 Model replicated to Dublin, Melbourne, and Cape Town — localized category mapping applied.',
    '📢 Shared “NextGen STEM” cluster to North America regional dashboard.',
    '💳 Adidas scholarship sync completed to São Paulo Agent cohort.',
    '📦 Inventory reallocated Toronto → Detroit for fall launch.',
    '📡 Manila Agent imported LA embeddings for transfer learning.',
    '🏫 Delhi Agent added 800-student STEM cohort for pilot testing.',
    '🧠 Tokyo Agent mirroring LA Trend Graph — cultural adaptation layer enabled.',
    '🌍 Global Node validated “Esports + Fitness Fusion” cross-market concept.',

    // 🧠 CONTINUOUS LEARNING & MODEL UPDATES
    '🧬 Feedback retrained Category Scorer v3.2 — margin > novelty weighting increased.',
    '📈 NLP Evaluator updated with 12K survey responses (auto sentiment tagging).',
    '🚨 Drift Detector triggered: LA trend shift toward creative arts (18% delta).',
    '🤖 Simulation Agent retrained on 217 pilot cases — baseline up 9%.',
    '📊 Model accuracy reached 91% across embeddings (cross-region validation).',
    '🧠 Tokyo Agent reached Tier 2 autonomy — promoted to regional learning coordinator.',
    '🧩 Retraining feedback pushed to HQ Node — propagated to all local agents.',
    '📈 12,000 QR scans analyzed — scheduling model refined +6%.',
    '🧠 Discovery feedback merged with MongoDB logs — context memory strengthened.',
    '📉 Removed low-performing category “Indoor Water Polo” — confidence below 0.32.',
    '🧩 Category Scorer now cross-references Google Trends + Meetup overlap rate.',
    '🌐 HQ Router benchmark complete — Gemini holds highest accuracy/token ratio at 0.73.',
    '🧱 Internal SLM Trainer initialized — learning routing heuristics from 3.2M DeepResearch logs.'
];



const getDummyLog = () => {
    const agents = Object.keys(agentToRegionMap);
    const statuses = Object.keys(statusColors);
    const message = messages[Math.floor(Math.random() * messages.length)];

    // Determine agent base
    let agentBase = agents.find(city => message.includes(city)) || 'Global';
    for (const key in synonymsMap) {
        if (message.includes(key)) agentBase = synonymsMap[key];
    }
    const region = agentToRegionMap[agentBase];

// Role mapping for metadata
    let role = agentRoles[Math.floor(Math.random() * agentRoles.length)];

    if (/(DeepResearch|Discovery|Trend|scraping|Google Trends|Meetup|embedding|Ablation|Router|Perplexity|Gemini|Claude|ChatGPT)/i.test(message))
        role = 'Analytics Agent';
    else if (/(Cluster|Concept|Graph|Scorer|Neural|Embedding|Topic|SLM Trainer)/i.test(message))
        role = 'Training Agent';
    else if (/(payment|invoice|refund|margin|budget|Stripe|Finance)/i.test(message))
        role = 'Finance Agent';
    else if (/(3PL|equipment|shipment|inventory|kit|banner|Supply Chain|logistics)/i.test(message))
        role = 'Logistics Agent';
    else if (/(email|Outreach|proposal|negotiation|call|contacted|SMS)/i.test(message))
        role = 'Outreach Agent';
    else if (/(sponsor|scholarship|grant|partnership|Adidas|Gatorade)/i.test(message))
        role = 'Sponsorship Agent';
    else if (/(campaign|CTA|marketing|ad rotation|promotion|TikTok|Puma|Nike)/i.test(message))
        role = 'Marketing Agent';
    else if (/(fraud|validation|locked|audit|compliance|risk)/i.test(message))
        role = 'Compliance Agent';
    else if (/(training|retraining|autonomy|learning|simulation|feedback|retraining)/i.test(message))
        role = 'Training Agent';
    else if (/(venue|manager|launch|planner|Facility|gym|school|permit|booking)/i.test(message))
        role = 'Booking Agent';


    const agent = `${agentBase} Agent`;
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const action = actions[Math.floor(Math.random() * actions.length)];
    const confidenceScore = `${Math.floor(80 + Math.random() * 20)}%`;
    const sessionId = `SS-${Math.floor(1000 + Math.random() * 9000)}`;

    return {
        time: new Date().toLocaleTimeString('en-US', { hour12: true }),
        agent,
        region,
        status,
        message,
        meta: {
            sessionId,
            action,
            confidenceScore,
            internalRole: role,
            executedBy: Math.random() > 0.5 ? 'Whook Core Engine' : 'Human Oversight',
            implication: status === 'learning' ? 'Training feedback loop' : 'Operational efficiency'
        },
        logs: [
            `[INFO] Event generated from ${Math.random() > 0.5 ? 'survey feedback' : 'QR scan analytics'}`,
            `[INFO] Evaluating coach pool, venue score, and lead density.`,
            `[DEBUG] Confidence drift observed: ${Math.floor(Math.random() * 20) + 80}% baseline.`,
            `[SUCCESS] ${status === 'executed' ? 'Action auto-executed' : 'Approval queued'} for ${agent}`
        ]
    };
};

const WhookStream = () => {
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [realTimeMode, setRealTimeMode] = useState(true);
    const [filters, setFilters] = useState({
        agent: 'All Agents',
        status: 'All Statuses',
        region: 'All Regions'
    });

    useEffect(() => {
        const init = Array.from({ length: 50 }, () => getDummyLog());
        setEvents(init.reverse());
    }, []);

    useEffect(() => {
        if (!realTimeMode) return;
        const interval = setInterval(() => {
            setEvents(prev => [getDummyLog(), ...prev.slice(0, 100)]);
        }, 1000);
        return () => clearInterval(interval);
    }, [realTimeMode]);

    const filteredEvents = events.filter(e => {
        const agentMatch = filters.agent === 'All Agents' || e.agent === filters.agent;
        const statusMatch = filters.status === 'All Statuses' || e.status === filters.status;
        const regionMatch = filters.region === 'All Regions' || e.region === filters.region;
        return agentMatch && statusMatch && regionMatch;
    });

    const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

    return (
        <Box sx={{ backgroundColor: "#121212", minHeight: "100vh", py: 6, px: 4 }}>
            <WhookStreamHeader
                realTimeMode={realTimeMode}
                onToggle={setRealTimeMode}
                filters={filters}
                onFilterChange={handleFilterChange}
            />
            <List dense>
                {filteredEvents.map((event, index) => (
                    <WhookEventItem key={index} event={event} onClick={setSelectedEvent} />
                ))}
            </List>
            <WhookEventModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
        </Box>
    );
};

export default WhookStream;
